import { TeoPersistableWritableStore, type ExtractWritablePropertyKeys } from '../state/TeoStore';
import type { PromptOptionTemplate } from '../TeoApi';
import { TeoCore } from '../TeoCore';
import { optionsToDropdownCategories } from './GeneratorUiHandler.helpers';
import type { SelectedOptions, TeoText } from './TeoText';
import { areAllPromptOptionsSelected } from './TeoText.helpers';
import type { TeoView } from './TextUi';

class TeoTextState {
  view: TeoView;
  previousView: Maybe<TeoView>;
  selectedPromptOptions: SelectedOptions;

  currentTemplateId: Maybe<string>;

  constructor(initialState: Partial<TeoTextState>) {
    this.view = initialState.view ?? 'performance';
    this.selectedPromptOptions = initialState.selectedPromptOptions ?? {};
    this.currentTemplateId = initialState.currentTemplateId ?? null;
    this.previousView = initialState.previousView ?? null;
  }

  get allPromptOptionsSelected(): boolean {
    return areAllPromptOptionsSelected(this.currentTemplate, this.selectedPromptOptions);
  }

  get dropdownCategories() {
    return optionsToDropdownCategories(this.currentTemplate?.options) ?? [];
  }

  get currentTemplate(): PromptOptionTemplate | null {
    return getTemplateById(this.currentTemplateId);
  }
}

export interface TeoTextStoredState {
  view: TeoView;
  previousView: Maybe<TeoView>;
  selectedPromptOptions: SelectedOptions;
  currentTemplateId?: Maybe<string>;
}

export class TeoTextStore extends TeoPersistableWritableStore<TeoTextState, TeoTextStoredState> {
  #text: TeoText;
  constructor(text: TeoText, initialState: TeoTextStoredState) {
    super(
      new TeoTextState({
        view: initialState.view,
        selectedPromptOptions: initialState.selectedPromptOptions ?? {},
        currentTemplateId: initialState.currentTemplateId ?? null,
        previousView: initialState.previousView ?? null,
      }),
    );
    this.#text = text;
    this.updateCurrentTemplateId();

    this.#text.core.dynamicConfig.addEventListener('promptOptionTemplates', () => {
      this.updateCurrentTemplateId();
    });
  }

  override setStateValue<K extends ExtractWritablePropertyKeys<TeoTextState>>(
    prop: K,
    value: TeoTextState[K],
    force = false,
  ) {
    if (prop === 'view' && value !== this.state.view) {
      super.setStateValue('previousView', value as Maybe<TeoView>);
    }
    return super.setStateValue(prop, value, force);
  }

  selectOption(id: string, value: string) {
    this.updateStateValue('selectedPromptOptions', (current) => ({
      ...current,
      [id]: value,
    }));
  }

  selectTemplate(id: string) {
    this.setStateValue('currentTemplateId', id);
  }

  toStorableState(): TeoTextStoredState {
    return structuredClone({
      view: this.state.view,
      previousView: this.state.previousView,
      selectedPromptOptions: this.state.selectedPromptOptions,
      currentTemplateId: this.state.currentTemplateId,
    });
  }

  private updateCurrentTemplateId() {
    const templates = TeoCore.current.dynamicConfig.options.promptOptionTemplates;
    if (!templates?.length) return;

    if (this.state.currentTemplateId) {
      const template = templates.find((t) => t.id === this.state.currentTemplateId);
      if (template) {
        // svelte needs the '*' event
        this.notifyChanged('currentTemplateId');
      }
    }
    // templateId does not exists or none was selected yet
    this.setStateValue('currentTemplateId', templates[0]?.id, true);
  }
}

function getTemplateById(id: Maybe<string>) {
  if (!id) return null;
  return (
    TeoCore.current.dynamicConfig.options.promptOptionTemplates?.find((t) => t.id === id) ?? null
  );
}
